import { toCsv } from './csv';
import { parseMarking } from '../model/marking';
import type { Equipment, ProjectDoc } from '../model/types';
import type { Derived } from '../state/derived';

/** Sources of release, with the columns of the data sheet for sources (cf. Table A.2). Raw SI numbers. */
export function scheduleCsv(doc: ProjectDoc, d: Derived): string {
  const area = new Map(doc.areas.map((a) => [a.id, a.name]));
  const sub = new Map(doc.substances.map((s) => [s.id, s.name]));
  const rows: Array<Array<string | number | undefined>> = [[
    'Tag', 'Description', 'Area', 'Substance', 'Grade', 'Release model', 'Wg (kg/s)', 'Qg (m3/s)', 'Qc (m3/s)', 'uw (m/s)',
    'Degree of dilution', 'Availability', 'Zone (Table D.1)', 'Zone drawn', 'Extent horizontal (m)', 'Extent vertical (m)', 'Fills room', 'Gas group', 'T-class', 'X (m)', 'Y (m)', 'Z (m)',
  ]];
  for (const s of doc.sources) {
    const r = d.results.sources[s.id];
    const room = r?.extent?.basis === 'room';
    rows.push([
      s.tag, s.description, area.get(s.areaId), sub.get(s.substanceId), s.grade, s.release.model,
      r?.release?.massRate, r?.release?.volumetricRate, r?.qc, r?.uw, r?.dilution, r?.availability, r?.outcomeLabel, r?.zoneLabel,
      room ? undefined : r?.extent?.horizontalM ?? undefined, room ? undefined : r?.extent?.verticalM ?? undefined, room ? 'yes' : 'no',
      r?.gasGroup, r?.tClass, ...s.position,
    ]);
  }
  return toCsv(rows);
}

export function equipmentCsv(doc: ProjectDoc, d: Derived): string {
  const rows: Array<Array<string | number | undefined>> = [['Tag', 'Description', 'Area', 'Marking', 'EPL', 'Gas group', 'T-class', 'In zone', 'Needs EPL', 'Needs gas group', 'Lowest AIT (C)', 'Result', 'Reasons', 'X (m)', 'Y (m)', 'Z (m)', 'Size X (m)', 'Size Y (m)', 'Size Z (m)']];
  const area = new Map(doc.areas.map((a) => [a.id, a.name]));
  for (const e of doc.equipment) {
    const c = d.checks[e.id];
    const ex = e.ex.kind === 'ex' ? e.ex : undefined;
    rows.push([
      e.tag, e.description, e.areaId ? area.get(e.areaId) : undefined, ex ? ex.marking ?? '' : 'Not Ex', ex?.epl, ex?.gasGroup, ex?.tClass,
      c?.required ? `Zone ${c.required.zone}` : 'None', c?.required?.epl, c?.required?.gasGroup, c?.required?.aitC,
      c?.status, c?.reasons.join(' '), ...e.box.center, ...e.box.size,
    ]);
  }
  return toCsv(rows);
}

export interface EquipmentImport {
  added: Equipment[];
  updated: Equipment[];
  problems: string[];
}

/**
 * Reads an equipment register. Needs a Tag column; uses Description, X, Y, Z, Size X/Y/Z, Marking and
 * Area when present (header names are matched loosely). Rows whose tag already exists update it.
 */
export function readEquipmentCsv(rows: string[][], doc: ProjectDoc, newId: () => string): EquipmentImport {
  const out: EquipmentImport = { added: [], updated: [], problems: [] };
  const [header, ...body] = rows;
  if (!header) return { ...out, problems: ['The file is empty.'] };
  const norm = (h: string) => h.toLowerCase().replace(/\(.*?\)/g, '').replace(/[^a-z]/g, '');
  const col = (...names: string[]) => header.findIndex((h) => names.includes(norm(h)));
  const c = { tag: col('tag', 'equipmenttag', 'id'), desc: col('description', 'desc'), x: col('x', 'centrex', 'centerx'), y: col('y', 'centrey', 'centery'), z: col('z', 'centrez', 'centerz', 'elevation'), sx: col('sizex', 'length'), sy: col('sizey', 'width'), sz: col('sizez', 'height'), marking: col('marking', 'exmarking'), area: col('area') };
  if (c.tag < 0) return { ...out, problems: ['No "Tag" column found in the first row.'] };
  const num = (r: string[], i: number) => (i >= 0 && r[i]?.trim() ? Number(r[i]!.replace(',', '.')) : undefined);
  const areaByName = new Map(doc.areas.map((a) => [a.name.toLowerCase(), a.id]));
  body.forEach((r, i) => {
    const tag = r[c.tag]?.trim();
    if (!tag) return void out.problems.push(`Row ${i + 2}: no tag; skipped.`);
    const existing = doc.equipment.find((e) => e.tag === tag);
    const x = num(r, c.x), y = num(r, c.y), z = num(r, c.z);
    if ([x, y, z].some((v) => v !== undefined && !Number.isFinite(v))) return void out.problems.push(`Row ${i + 2} (${tag}): position is not a number; skipped.`);
    const marking = c.marking >= 0 ? r[c.marking]?.trim() ?? '' : undefined;
    const ex: Equipment['ex'] | undefined = marking === undefined ? undefined
      : !marking || /^(non[- ]?ex|not ex|none|-)$/i.test(marking) ? { kind: 'non-ex' }
      : (() => { const p = parseMarking(marking); return p.epl && p.gasGroup ? { kind: 'ex' as const, epl: p.epl, gasGroup: p.gasGroup, ...(p.tClass ? { tClass: p.tClass } : {}), ...(p.maxSurfaceTempC ? { maxSurfaceTempC: p.maxSurfaceTempC } : {}), marking } : undefined; })();
    if (marking && !ex) out.problems.push(`Row ${i + 2} (${tag}): could not read the protection level and gas group from "${marking}"; marked as not Ex.`);
    const areaId = c.area >= 0 ? areaByName.get((r[c.area] ?? '').trim().toLowerCase()) : undefined;
    const base: Equipment = existing ? structuredClone(existing) : { id: newId(), tag, description: '', box: { center: [0, 0, 0.5], size: [0.4, 0.4, 0.4], yawRad: 0 }, ex: { kind: 'non-ex' } };
    if (c.desc >= 0) base.description = r[c.desc]?.trim() ?? base.description;
    base.box.center = [x ?? base.box.center[0], y ?? base.box.center[1], z ?? base.box.center[2]];
    base.box.size = [num(r, c.sx) ?? base.box.size[0], num(r, c.sy) ?? base.box.size[1], num(r, c.sz) ?? base.box.size[2]];
    if (ex) base.ex = ex;
    else if (marking) base.ex = { kind: 'non-ex' };
    if (areaId) base.areaId = areaId;
    (existing ? out.updated : out.added).push(base);
  });
  return out;
}
